import { useEffect, useRef, useState } from 'react';
import { Socket } from 'socket.io-client';

import { useConsultationSocket } from '../../connectivity/chat/ConsultationSocketProvider';

interface GracePeriodStartedPayload {
  sessionId: string;
  disconnectedRole: 'user' | 'expert';
  disconnectedName: string;
  remainingSeconds: number;
  expiresAt: number;
}

interface GracePeriodCancelledPayload {
  sessionId: string;
  reconnectedRole: 'user' | 'expert';
}

interface UseGracePeriodAlertProps {
  sessionId: string;
  senderCode: string;
  senderType: 'user' | 'expert';

  // Optional existing socket.
  // ExpertSocketProvider can pass its existing persistent socket.
  // User side uses ConsultationSocketProvider.
  socket?: Socket | null;
}

export const useGracePeriodAlert = ({
  sessionId,
  senderCode,
  senderType,
  socket: externalSocket = null,
}: UseGracePeriodAlertProps) => {
  const [isGracePeriodActive, setIsGracePeriodActive] =
    useState(false);

  const [disconnectedRole, setDisconnectedRole] = useState<
    'user' | 'expert' | null
  >(null);

  const [disconnectedName, setDisconnectedName] =
    useState('');

  const [remainingSeconds, setRemainingSeconds] =
    useState(0);

  const socketRef = useRef<Socket | null>(null);

  const expiresAtRef = useRef<number | null>(null);

  const intervalRef =
    useRef<ReturnType<typeof setInterval> | null>(null);

  /*
   * User-side consultation socket.
   *
   * This socket is owned by ConsultationSocketProvider.
   * The hook only attaches/removes its own listeners.
   */
  const { socket: consultationSocket } =
    useConsultationSocket();

  /*
   * Expert can provide its existing ExpertSocketProvider socket.
   *
   * User uses the persistent ConsultationSocketProvider socket.
   */
  const socket =
    externalSocket || consultationSocket;

  const clearGracePeriod = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    expiresAtRef.current = null;

    setIsGracePeriodActive(false);
    setDisconnectedRole(null);
    setDisconnectedName('');
    setRemainingSeconds(0);
  };

  useEffect(() => {
    if (!sessionId || !senderCode || !socket) {
      return;
    }

    socketRef.current = socket;

    const handleGracePeriodStarted = (
      data: GracePeriodStartedPayload,
    ) => {
      if (!data || data.sessionId !== sessionId) {
        return;
      }

      if (
        data.disconnectedRole !== 'user' &&
        data.disconnectedRole !== 'expert'
      ) {
        return;
      }

      const expiresAt =
        Number(data.expiresAt) ||
        Date.now() +
          Number(data.remainingSeconds || 0) * 1000;

      /*
       * Ignore an older grace-period event.
       */
      if (
        expiresAtRef.current !== null &&
        expiresAt <= expiresAtRef.current
      ) {
        return;
      }

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      expiresAtRef.current = expiresAt;

      setDisconnectedRole(data.disconnectedRole);

      setDisconnectedName(
        data.disconnectedName || 'Participant',
      );

      setIsGracePeriodActive(true);

      const updateRemainingTime = () => {
        const remaining = Math.max(
          0,
          Math.ceil(
            (expiresAt - Date.now()) / 1000,
          ),
        );

        setRemainingSeconds(remaining);

        if (remaining <= 0) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }

          expiresAtRef.current = null;

          setIsGracePeriodActive(false);
          setDisconnectedRole(null);
          setDisconnectedName('');
          setRemainingSeconds(0);
        }
      };

      updateRemainingTime();

      intervalRef.current = setInterval(
        updateRemainingTime,
        1000,
      );
    };

    const handleGracePeriodCancelled = (
      data: GracePeriodCancelledPayload,
    ) => {
      if (!data || data.sessionId !== sessionId) {
        return;
      }

      clearGracePeriod();
    };

    const handleSessionEnded = (
      data: { reason?: string },
    ) => {
      if (!data) {
        return;
      }

      clearGracePeriod();
    };

    /*
     * Listen to the existing persistent socket.
     */
    socket.on(
      'grace_period_started',
      handleGracePeriodStarted,
    );

    socket.on(
      'grace_period_cancelled',
      handleGracePeriodCancelled,
    );

    socket.on(
      'session_ended',
      handleSessionEnded,
    );

    return () => {
      /*
       * Remove ONLY this hook's listeners.
       *
       * Do not disconnect the shared socket.
       */
      socket.off(
        'grace_period_started',
        handleGracePeriodStarted,
      );

      socket.off(
        'grace_period_cancelled',
        handleGracePeriodCancelled,
      );

      socket.off(
        'session_ended',
        handleSessionEnded,
      );

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      expiresAtRef.current = null;

      /*
       * The socket belongs to the persistent provider.
       * This hook must never disconnect it.
       */
      if (socketRef.current === socket) {
        socketRef.current = null;
      }
    };
  }, [
    sessionId,
    senderCode,
    senderType,
    socket,
  ]);

  const formattedRemainingTime = `${Math.floor(
    remainingSeconds / 60,
  )
    .toString()
    .padStart(2, '0')}:${(
    remainingSeconds % 60
  )
    .toString()
    .padStart(2, '0')}`;

  return {
    isGracePeriodActive,
    disconnectedRole,
    disconnectedName,
    remainingSeconds,
    formattedRemainingTime,
    clearGracePeriod,
  };
};